import { useState, useEffect, createContext, useContext, useRef, useCallback, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import BetaBanner from "./components/BetaBanner";

// Lazy loaded pages
const BetaAdmin = lazy(() => import("./pages/BetaAdmin"));
const DatabaseAdmin = lazy(() => import("./pages/DatabaseAdmin"));
const Landing = lazy(() => import("./pages/Landing"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Discovery = lazy(() => import("./pages/Discovery"));
const Matches = lazy(() => import("./pages/Matches"));
const Chat = lazy(() => import("./pages/Chat"));
const Profile = lazy(() => import("./pages/Profile"));
const EditProfile = lazy(() => import("./pages/EditProfile"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const Settings = lazy(() => import("./pages/Settings"));
const MatchDetail = lazy(() => import("./pages/MatchDetail"));
const Verification = lazy(() => import("./pages/Verification"));
const LikesBank = lazy(() => import("./pages/LikesBank"));
const Subscription = lazy(() => import("./pages/Subscription"));
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const Messages = lazy(() => import("./pages/Messages"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// Axios interceptor
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("vynco_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Standard Web Push helper — converts a VAPID URL-safe base64 public key
// to the Uint8Array format required by pushManager.subscribe().
// See: https://developer.mozilla.org/en-US/docs/Web/API/PushManager/subscribe
function urlBase64ToUint8Array(base64String) {
  if (typeof base64String !== 'string') {
    throw new Error('VAPID key must be a string');
  }
  // Sanitize: strip whitespace, BOM, quotes, and any non-base64url characters
  const clean = base64String
    .trim()
    .replace(/^\uFEFF/, '')
    .replace(/^["']+|["']+$/g, '')
    .replace(/[^A-Za-z0-9\-_]/g, '');

  const padding = '='.repeat((4 - (clean.length % 4)) % 4);
  const base64 = (clean + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Push notification subscription
async function subscribeToPush() {
  try {
    if (!('serviceWorker' in navigator)) {
      console.log('Service Workers not supported');
      return false;
    }
    if (!('PushManager' in window)) {
      console.log('Push API not supported');
      return false;
    }
    if (!('Notification' in window)) {
      console.log('Notifications not supported');
      return false;
    }
    
    // Register service worker (force fresh fetch, no HTTP cache)
    let registration;
    try {
      registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none'
      });
      // Force update check (so Android Chrome picks up new SW versions)
      try { await registration.update(); } catch (_) {}
      registration = await navigator.serviceWorker.ready;
      // If a new SW is waiting, activate it immediately
      if (registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
    } catch (swErr) {
      console.error('SW registration failed:', swErr);
      return false;
    }

    // Request permission (triggers browser popup)
    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    if (permission !== 'granted') return false;

    // Check existing subscription
    let subscription = await registration.pushManager.getSubscription();

    // If subscription exists but VAPID key may have changed, ditch it and resubscribe
    if (subscription) {
      try {
        const currentKey = subscription.options && subscription.options.applicationServerKey;
        if (!currentKey) {
          await subscription.unsubscribe();
          subscription = null;
        }
      } catch (_) {}
    }
    
    if (!subscription) {
      // Get VAPID public key from backend
      const vapidRes = await axios.get(`${API}/push/vapid-key`);
      const vapidKey = vapidRes && vapidRes.data && vapidRes.data.public_key;
      if (!vapidKey || typeof vapidKey !== 'string') {
        console.error('[Push] VAPID public key missing or invalid from backend:', vapidRes?.data);
        return false;
      }
      console.log('[Push] VAPID key length:', vapidKey.length, 'preview:', vapidKey.substring(0, 20));

      // Convert URL-safe base64 VAPID key to Uint8Array (Web Push standard)
      let applicationServerKey;
      try {
        applicationServerKey = urlBase64ToUint8Array(vapidKey);
        console.log('[Push] applicationServerKey byteLength:', applicationServerKey.byteLength);
      } catch (convErr) {
        console.error('[Push] urlBase64ToUint8Array failed:', convErr);
        return false;
      }

      // Uncompressed P-256 public key must be exactly 65 bytes
      if (applicationServerKey.byteLength !== 65) {
        console.error('[Push] VAPID key wrong byte length, expected 65 got', applicationServerKey.byteLength);
        return false;
      }

      // Create subscription
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey
        });
      } catch (subErr) {
        console.error('[Push] pushManager.subscribe failed:', subErr);
        return false;
      }
    }
    
    // Envía al backend (si falla, marcamos como no suscrito y propagamos el error)
    const subJson = subscription.toJSON();
    console.log('[Push] endpoint:', subJson.endpoint?.substring(0, 60));
    try {
      const res = await axios.post(`${API}/push/subscribe`, { subscription: subJson });
      console.log('[Push] backend confirmed subscription:', res.data);
      return true;
    } catch (postErr) {
      console.error('[Push] backend rejected subscription:', postErr?.response?.status, postErr?.response?.data);
      return false;
    }
  } catch (e) {
    console.error('Push subscription error:', e);
    return false;
  }
}

async function unsubscribeFromPush() {
  try {
    if (!('serviceWorker' in navigator)) return;
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
    }
    await axios.post(`${API}/push/unsubscribe`);
  } catch (e) {
    console.error('Push unsubscribe error:', e);
  }
}

// Inactivity timeout (30 minutes)
const INACTIVITY_TIMEOUT = 30 * 60 * 1000;

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState(localStorage.getItem("vynco_lang") || "es");
  const [pushEnabled, setPushEnabled] = useState(false);
  const inactivityTimer = useRef(null);

  const performLogout = useCallback(() => {
    localStorage.removeItem("vynco_token");
    setUser(null);
    setPushEnabled(false);
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
  }, []);

  // Inactivity auto-logout
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      if (localStorage.getItem("vynco_token")) {
        performLogout();
        window.location.href = '/login';
      }
    }, INACTIVITY_TIMEOUT);
  }, [performLogout]);

  useEffect(() => {
    if (!user) return;
    
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    const handler = () => resetInactivityTimer();
    
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    resetInactivityTimer();
    
    return () => {
      events.forEach(e => window.removeEventListener(e, handler));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, [user, resetInactivityTimer]);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("vynco_token");
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
      // Auto-subscribe to push if permission was already granted
      if ('Notification' in window && Notification.permission === 'granted') {
        const result = await subscribeToPush();
        setPushEnabled(result);
      }
    } catch (e) {
      localStorage.removeItem("vynco_token");
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("vynco_token", res.data.token);
    setUser(res.data.user);
    // Subscribe to push after login
    const result = await subscribeToPush();
    setPushEnabled(result);
    return res.data.user;
  };

  const register = async (email, password, name) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name });
    localStorage.setItem("vynco_token", res.data.token);
    setUser(res.data.user);
    // Subscribe to push after register too (so new users get notifications)
    try {
      const result = await subscribeToPush();
      setPushEnabled(result);
    } catch (_) {}
    return res.data.user;
  };

  const logout = () => {
    unsubscribeFromPush();
    performLogout();
  };

  const togglePush = async () => {
    if (pushEnabled) {
      await unsubscribeFromPush();
      setPushEnabled(false);
      return false;
    } else {
      const result = await subscribeToPush();
      setPushEnabled(result);
      return result;
    }
  };

  const updateUser = (data) => {
    setUser((prev) => ({ ...prev, ...data }));
  };

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem("vynco_lang", lang);
  };

  return (
    <AuthContext.Provider value={{ 
      user, loading, login, register, logout, updateUser, 
      language, changeLanguage, isAuthenticated: !!user,
      pushEnabled, togglePush
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF7F5] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-rose-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!user.onboarding_completed) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// Onboarding Route
const OnboardingRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF7F5] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-rose-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.onboarding_completed) {
    return <Navigate to="/discover" replace />;
  }

  return children;
};

import NotificationPrompt from "./components/NotificationPrompt";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <BetaBanner />
        <NotificationPrompt />
        <Toaster 
          position="top-center" 
          richColors 
          theme="light"
          toastOptions={{
            style: {
              background: '#FFFFFF',
              border: '1px solid #E8E2DE',
              color: '#2D2A28'
            }
          }}
        />
        <Suspense fallback={
          <div className="min-h-screen bg-[#FAF7F5] flex items-center justify-center">
            <div className="w-12 h-12 border-4 border-rose-600 border-t-transparent rounded-full animate-spin" />
          </div>
        }>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/onboarding" element={
            <OnboardingRoute><Onboarding /></OnboardingRoute>
          } />
          <Route path="/discover" element={
            <ProtectedRoute><Discovery /></ProtectedRoute>
          } />
          <Route path="/likes" element={
            <ProtectedRoute><LikesBank /></ProtectedRoute>
          } />
          <Route path="/matches" element={
            <ProtectedRoute><Matches /></ProtectedRoute>
          } />
          <Route path="/messages" element={
            <ProtectedRoute><Messages /></ProtectedRoute>
          } />
          <Route path="/matches/:matchId" element={
            <ProtectedRoute><MatchDetail /></ProtectedRoute>
          } />
          <Route path="/chat/:matchId" element={
            <ProtectedRoute><Chat /></ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute><Profile /></ProtectedRoute>
          } />
          <Route path="/profile/edit" element={
            <ProtectedRoute><EditProfile /></ProtectedRoute>
          } />
          <Route path="/profile/:userId" element={
            <ProtectedRoute><UserProfile /></ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute><Settings /></ProtectedRoute>
          } />
          <Route path="/verification" element={
            <ProtectedRoute><Verification /></ProtectedRoute>
          } />
          <Route path="/subscription" element={
            <ProtectedRoute><Subscription /></ProtectedRoute>
          } />
          <Route path="/subscription/success" element={
            <ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>
          } />
          <Route path="/admin/beta" element={
            <ProtectedRoute><BetaAdmin /></ProtectedRoute>
          } />
          <Route path="/admin/database" element={
            <ProtectedRoute><DatabaseAdmin /></ProtectedRoute>
          } />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
